import { DatePipe, DecimalPipe, LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { Release, ReleaseStatus } from '../../models/release';
import { activeReleasesByStart, releaseStartDate } from '../../services/release/release-sort';
import { ReleaseService } from '../../services/release/release.service';

type StatusFilter = 'All' | ReleaseStatus;

/** Pass rate at or above this renders normally; below renders in the warn color. */
const PASS_RATE_WARN_THRESHOLD = 90;

@Component({
  selector: 'app-releases',
  standalone: true,
  imports: [DatePipe, DecimalPipe, LowerCasePipe, RouterLink],
  templateUrl: './releases.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './releases.component.scss',
})
export class ReleasesComponent implements OnInit {
  private releaseService = inject(ReleaseService);

  readonly warnThreshold = PASS_RATE_WARN_THRESHOLD;
  readonly statusChips: StatusFilter[] = ['All', 'Shipped', 'Held', 'Archived'];

  /** Placeholder rows rendered while releases load. */
  readonly skeletonRows = [0, 1, 2, 3];

  readonly releases = signal<Release[]>([]);
  readonly isLoading = signal(false);
  /** Set when the releases request fails, so the page can show a failure state. */
  readonly loadError = signal(false);
  readonly searchTerm = signal('');
  readonly statusFilter = signal<StatusFilter>('All');

  /** Every active release, each shown as its own hero card, earliest start first. */
  readonly activeReleases = computed(() => activeReleasesByStart(this.releases()));

  /** Non-active releases, newest first. */
  readonly pastReleases = computed(() =>
    this.releases()
      .filter((r) => !r.isActive)
      .sort((a, b) => +new Date(b.createdOnUtc) - +new Date(a.createdOnUtc)),
  );

  readonly statusCounts = computed(() => {
    const past = this.pastReleases();
    return {
      All: past.length,
      Shipped: past.filter((r) => r.status === 'Shipped').length,
      Held: past.filter((r) => r.status === 'Held').length,
      Archived: past.filter((r) => r.status === 'Archived').length,
    } as Record<StatusFilter, number>;
  });

  readonly filteredPastReleases = computed(() => {
    const status = this.statusFilter();
    const term = this.searchTerm().trim().toLowerCase();
    return this.pastReleases()
      .filter((r) => status === 'All' || r.status === status)
      .filter((r) => !term || r.releaseName.toLowerCase().includes(term));
  });

  ngOnInit(): void {
    this.loadReleases();
  }

  /** Clear the search box and reset the status chips back to "All". */
  resetFilters(): void {
    this.searchTerm.set('');
    this.statusFilter.set('All');
  }

  // ---- per-release derived numbers ----

  /** Start date shown on the hero, so the dates read in the same order as the cards. */
  startDate(r: Release): string {
    return releaseStartDate(r);
  }

  testsRun(r: Release): number {
    return r.numberOfPassingTests + r.numberOfFailingTests;
  }

  remaining(r: Release): number {
    return r.numberOfTests - r.numberOfPassingTests - r.numberOfFailingTests;
  }

  /** Percent of all tests that have a result (brand fill in the hero bar). */
  completedPct(r: Release): number {
    return r.numberOfTests > 0 ? (this.testsRun(r) / r.numberOfTests) * 100 : 0;
  }

  passingPct(r: Release): number {
    return r.numberOfTests > 0 ? (r.numberOfPassingTests / r.numberOfTests) * 100 : 0;
  }

  failingPct(r: Release): number {
    return r.numberOfTests > 0 ? (r.numberOfFailingTests / r.numberOfTests) * 100 : 0;
  }

  /** Pass rate over executed tests (passing / tests run). */
  passRate(r: Release): number {
    const run = this.testsRun(r);
    return run > 0 ? (r.numberOfPassingTests / run) * 100 : 0;
  }

  /** Public so the failure state's "Try again" button can re-run the request. */
  reload(): void {
    this.loadReleases();
  }

  private loadReleases(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.releaseService
      .getReleases()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (releases) => this.releases.set(releases),
        error: (err) => {
          // The inline failure state (with Try again) is the only surface here;
          // no toast, so we don't double up on the error message.
          console.error('Error loading releases:', err);
          this.loadError.set(true);
        },
      });
  }
}
